/* eslint-disable */
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*------ import url and method ------*/
import * as url from "helper/UrlHelper";
import { putData, getData } from "utils/Gateway";
import { getAllCategories } from "helper/CategoryHelper/CategoryHelper";

/*------ import components ------*/
import BreadCrumb from "components/Common/BreadCrumb/BreadCrumb";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";

// A self-contained, reusable column for the category tree
const CategoryColumn = ({
  title,
  items = [],
  selected,
  onSelect,
  isLoading,
}) => {
  return (
    <div
      className="col-lg-2 col-md-4 col-sm-6 border-end"
      style={{ minWidth: "220px" }}
    >
      <div className="p-3">
        <h6 className="fw-bold text-muted text-uppercase small mb-3">
          {title}
        </h6>
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "300px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : items.length > 0 ? (
          <ul className="list-unstyled">
            {items.map((item) => (
              <li key={item._id} className="mb-1">
                <Link
                  to="#"
                  className={`d-flex justify-content-between align-items-center p-2 rounded-3 text-decoration-none transition-all ${
                    selected === item._id
                      ? "bg-primary bg-opacity-10 text-primary fw-bold"
                      : "text-dark"
                  }`}
                  onClick={() => onSelect(item.slug, item._id)}
                >
                  <span className="fs-sm">{item.name}</span>
                  <span className="material-symbols-outlined icon-sm">
                    chevron_right
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center text-center text-muted"
            style={{ minHeight: "300px" }}
          >
            <p className="fs-sm">No more sub-categories.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const TreeView = () => {
  const { t } = useTranslation();
  const params = useParams();
  const token = localStorage.getItem("token");
  const ecosystemSlug = localStorage.getItem("ecosystemslug");

  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // States for category data at each level
  const [parentbaseCategories, setParentBaseCategories] = useState([]);
  const [levelTwoCategories, setLevelTwoCategories] = useState([]);
  const [levelThreeCategories, setLevelThreeCategories] = useState([]);
  const [levelFourCategories, setLevelFourCategories] = useState([]);
  const [levelFiveCategories, setLevelFiveCategories] = useState([]);
  const [levelSixCategories, setLevelSixCategories] = useState([]);

  // States for selected category ID at each level
  const [selectedBaseCategoryId, setSelectedBaseCategoryId] = useState(null);
  const [selectedLevelTwoCategoryId, setSelectedLevelTwoCategoryId] =
    useState(null);
  const [selectedLevelThreeCategoryId, setSelectedLevelThreeCategoryId] =
    useState(null);
  const [selectedLevelFourCategoryId, setSelectedLevelFourCategoryId] =
    useState(null);
  const [selectedLevelFiveCategoryId, setSelectedLevelFiveCategoryId] =
    useState(null);
  const [selectedLevelSixCategoryId, setSelectedLevelSixCategoryId] =
    useState(null);

  // Loading states for each column
  const [loadingStates, setLoadingStates] = useState({
    levelOne: true,
    levelTwo: false,
    levelThree: false,
    levelFour: false,
    levelFive: false,
    levelSix: false,
  });

  // Validation state
  const [validation, setValidation] = useState({ categoryWarning: false });

  // Alert states
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const breadcrumbText = [
    { title: t("Products"), link: "/admin/marketplace/products/list" },
    { title: name || "Select Category" },
  ];

  const validationHandler = () => {
    const isValid = selectedBaseCategoryId !== null;
    setValidation({ categoryWarning: !isValid });
    return isValid;
  };

  const getProductDetails = async () => {
    try {
      setIsInitialLoading(true);

      const requestUrl = `${url.API_BASE_URL}${url.API_GET_PRODUCT_DETAILS}/${params.id}`;
      const response = await getData(requestUrl);

      if (response.status && response.data) {
        const data = response.data;
        setName(data.name);

        if (data.categoryids && data.categoryids.length > 0) {
          const handlers = [
            (slug, id) => parentSelectionHandler(slug, id, true),
            (slug, id) => childOneSelectionHandler(slug, id, true),
            (slug, id) => childTwoSelectionHandler(slug, id, true),
            (slug, id) => childThreeSelectionHandler(slug, id, true),
            (slug, id) => childFourSelectionHandler(slug, id, true),
            (slug, id) => childFiveSelectionHandler(slug, id, true),
          ];

          const baseCategories = await getAllCategories(
            ecosystemSlug,
            "PRODUCT",
          );
          const matchingBase = baseCategories.find((cat) =>
            data.categoryids.includes(cat._id),
          );

          if (matchingBase) {
            await handlers[0](matchingBase.slug, matchingBase._id);

            let currentSlug = matchingBase.slug;
            for (let i = 1; i < handlers.length; i++) {
              const childCategories = await getAllCategories(
                ecosystemSlug,
                currentSlug,
              );
              const matchingChild = childCategories.find((cat) =>
                data.categoryids.includes(cat._id),
              );
              if (matchingChild) {
                await handlers[i](matchingChild.slug, matchingChild._id);
                currentSlug = matchingChild.slug;
              } else {
                break;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Failed to get product details:", error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const getAllCategoryList = async (parentSlug = "PRODUCT", targetLevel) => {
    setLoadingStates((prev) => ({ ...prev, [targetLevel]: true }));
    try {
      const response = await getAllCategories(ecosystemSlug, parentSlug);
      const sortedResponse = response.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );

      switch (targetLevel) {
        case "levelOne":
          setParentBaseCategories(sortedResponse);
          break;
        case "levelTwo":
          setLevelTwoCategories(sortedResponse);
          break;
        case "levelThree":
          setLevelThreeCategories(sortedResponse);
          break;
        case "levelFour":
          setLevelFourCategories(sortedResponse);
          break;
        case "levelFive":
          setLevelFiveCategories(sortedResponse);
          break;
        case "levelSix":
          setLevelSixCategories(sortedResponse);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [targetLevel]: false }));
    }
  };

  const resetLevels = (fromLevel) => {
    if (fromLevel <= 2) {
      setSelectedLevelTwoCategoryId(null);
      setLevelTwoCategories([]);
    }
    if (fromLevel <= 3) {
      setSelectedLevelThreeCategoryId(null);
      setLevelThreeCategories([]);
    }
    if (fromLevel <= 4) {
      setSelectedLevelFourCategoryId(null);
      setLevelFourCategories([]);
    }
    if (fromLevel <= 5) {
      setSelectedLevelFiveCategoryId(null);
      setLevelFiveCategories([]);
    }
    if (fromLevel <= 6) {
      setSelectedLevelSixCategoryId(null);
      setLevelSixCategories([]);
    }
  };

  const parentSelectionHandler = (slug, id, isPreloading = false) => {
    setSelectedBaseCategoryId(id);
    resetLevels(2);
    if (!isPreloading) validationHandler();
    getAllCategoryList(slug, "levelTwo");
  };

  const childOneSelectionHandler = (slug, id, isPreloading = false) => {
    setSelectedLevelTwoCategoryId(id);
    resetLevels(3);
    getAllCategoryList(slug, "levelThree");
  };

  const childTwoSelectionHandler = (slug, id, isPreloading = false) => {
    setSelectedLevelThreeCategoryId(id);
    resetLevels(4);
    getAllCategoryList(slug, "levelFour");
  };

  const childThreeSelectionHandler = (slug, id, isPreloading = false) => {
    setSelectedLevelFourCategoryId(id);
    resetLevels(5);
    getAllCategoryList(slug, "levelFive");
  };

  const childFourSelectionHandler = (slug, id, isPreloading = false) => {
    setSelectedLevelFiveCategoryId(id);
    resetLevels(6);
    getAllCategoryList(slug, "levelSix");
  };

  const childFiveSelectionHandler = (slug, id, isPreloading = false) => {
    setSelectedLevelSixCategoryId(id);
  };

  const saveProductInfoHandler = async () => {
    if (!validationHandler()) return;

    setIsSaving(true);
    try {
      const categoryIds = [
        selectedBaseCategoryId,
        selectedLevelTwoCategoryId,
        selectedLevelThreeCategoryId,
        selectedLevelFourCategoryId,
        selectedLevelFiveCategoryId,
        selectedLevelSixCategoryId,
      ].filter(Boolean); // Filter out null values

      const requestUrl = `${url.API_BASE_URL}${url.API_UPDATE_PRODUCT}/${params.id}`;
      const response = await putData(requestUrl, { categoryids: categoryIds });

      if (response.status) {
        setAlertMessage(t("Category updated successfully!"));
        setMessageType("success");
      } else {
        setAlertMessage(response.message || t("Failed to update category."));
        setMessageType("error");
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
    } finally {
      setIsSaving(false);
      setShowAlert(true);
    }
  };

  useEffect(() => {
    if (params.id) {
      getAllCategoryList("PRODUCT", "levelOne");
      getProductDetails();
    }
  }, [params.id]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  return (
    <div className="container-fluid px-lg-4">
      <div className="py-3">
        <div className="d-flex align-items-center justify-content-between">
          <BreadCrumb breadCrumbText={breadcrumbText} />
          <div className="action">
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={saveProductInfoHandler}
              disabled={isSaving}
            >
              <span className="material-symbols-outlined icon-sm">save</span>
              {isSaving ? t("Saving...") : t("Save Changes")}
              {isSaving && (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        {/* <div className="card-header bg-light p-3">
          <h5 className="card-title mb-0">{t("Select Product Category")}</h5>
        </div> */}
        <div className="card-body p-0">
          {isInitialLoading ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center gap-4"
              style={{ minHeight: "400px" }}
            >
              <div className="d-flex align-items-center gap-2">
                {["Base", "L2", "L3", "L4", "L5", "L6"].map((label, index) => (
                  <div key={index} className="d-flex align-items-center gap-2">
                    <div
                      className="d-flex flex-column align-items-center gap-1"
                      style={{
                        animation: `fadeInUp 0.4s ease forwards`,
                        animationDelay: `${index * 0.3}s`,
                        opacity: 0,
                      }}
                    >
                      <div
                        className="rounded-circle bg-primary bg-opacity-10 border border-primary d-flex align-items-center justify-content-center"
                        style={{ width: "48px", height: "48px" }}
                      >
                        <div
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                          style={{ animationDelay: `${index * 0.15}s` }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                      <span
                        className="text-primary fw-semibold"
                        style={{ fontSize: "11px" }}
                      >
                        {label}
                      </span>
                    </div>
                    {index < 5 && (
                      <div
                        className="bg-primary bg-opacity-25"
                        style={{
                          width: "32px",
                          height: "2px",
                          marginBottom: "18px",
                          animation: `expandWidth 0.3s ease forwards`,
                          animationDelay: `${index * 0.3 + 0.2}s`,
                          transform: "scaleX(0)",
                          transformOrigin: "left",
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-muted fs-sm mb-0">
                {t("Loading categories, please wait...")}
              </p>
            </div>
          ) : (
            <div className="overflow-auto">
              <div className="row g-0 flex-nowrap">
                <CategoryColumn
                  title="Base Category"
                  items={parentbaseCategories}
                  onSelect={parentSelectionHandler}
                  selected={selectedBaseCategoryId}
                  isLoading={loadingStates.levelOne}
                />
                {(selectedBaseCategoryId || loadingStates.levelTwo) && (
                  <CategoryColumn
                    title="Level 2"
                    items={levelTwoCategories}
                    onSelect={childOneSelectionHandler}
                    selected={selectedLevelTwoCategoryId}
                    isLoading={loadingStates.levelTwo}
                  />
                )}
                {(selectedLevelTwoCategoryId || loadingStates.levelThree) && (
                  <CategoryColumn
                    title="Level 3"
                    items={levelThreeCategories}
                    onSelect={childTwoSelectionHandler}
                    selected={selectedLevelThreeCategoryId}
                    isLoading={loadingStates.levelThree}
                  />
                )}
                {(selectedLevelThreeCategoryId || loadingStates.levelFour) && (
                  <CategoryColumn
                    title="Level 4"
                    items={levelFourCategories}
                    onSelect={childThreeSelectionHandler}
                    selected={selectedLevelFourCategoryId}
                    isLoading={loadingStates.levelFour}
                  />
                )}
                {(selectedLevelFourCategoryId || loadingStates.levelFive) && (
                  <CategoryColumn
                    title="Level 5"
                    items={levelFiveCategories}
                    onSelect={childFourSelectionHandler}
                    selected={selectedLevelFiveCategoryId}
                    isLoading={loadingStates.levelFive}
                  />
                )}
                {(selectedLevelFiveCategoryId || loadingStates.levelSix) && (
                  <CategoryColumn
                    title="Level 6"
                    items={levelSixCategories}
                    onSelect={childFiveSelectionHandler}
                    selected={selectedLevelSixCategoryId}
                    isLoading={loadingStates.levelSix}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {validation.categoryWarning && (
          <div className="card-footer bg-danger bg-opacity-10 text-danger">
            <p className="d-flex align-items-center gap-2 mb-0 fs-sm">
              <span className="material-symbols-outlined">warning</span>
              <span>
                {t("Please select at least one base category to proceed.")}
              </span>
            </p>
          </div>
        )}
      </div>

      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={onAlertClose}
        />
      )}

      <style>{`@keyframes fadeInUp {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes expandWidth {
      from { transform: scaleX(0); }
      to   { transform: scaleX(1); }
    }`}</style>
    </div>
  );
};

export default TreeView;
